import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { PROJECTS, DISCIPLINES } from "@/data/projects";
import { useSEO, breadcrumb } from "@/lib/useSEO";

export default function Projects() {
  const [filter, setFilter] = useState("All");
  useSEO({
    title:
      "The Archive · Completed Luxury Flooring Installations — My Floor Studio",
    description:
      "A living catalogue of completed luxury flooring installations across Sydney and Australia — organised by element and read by finish. Timber, Carpet, Tile and Stone case studies.",
    path: "/archive",
    keywords:
      "luxury flooring projects, timber flooring installations, marble tile projects, natural stone case studies, Sydney architect projects, interior design portfolio, engineered oak installations",
    imageAlt:
      "The Archive — completed luxury flooring installations by My Floor Studio",
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Archive", path: "/archive" },
      ]),
    ],
  });
  const list = useMemo(
    () => (filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.discipline === filter)),
    [filter]
  );

  return (
    <div className="min-h-screen bg-[#121315] grain">
      <Nav />
      <main className="pt-32 md:pt-40">
        <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-16">
          <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-8">The Archive</p>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <h1 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-5xl sm:text-6xl md:text-7xl xl:text-8xl leading-[0.95] max-w-3xl">
              Every finish, realised.
            </h1>
            <p className="text-[#D6D1CA]/70 font-body max-w-sm text-sm md:text-base">
              A living catalogue of completed installations — organised by element,
              read by finish.
            </p>
          </div>

          {/* filter bar */}
          <div className="mt-14 flex flex-wrap gap-3" data-testid="projects-filter">
            {DISCIPLINES.map((d) => {
              const on = filter === d;
              return (
                <button
                  key={d}
                  data-testid={`filter-${d.toLowerCase()}`}
                  onClick={() => setFilter(d)}
                  className="rounded-full border px-6 py-2 text-[11px] tracking-[0.2em] uppercase"
                  style={{
                    borderColor: on ? "#8C7355" : "rgba(214,209,202,0.22)",
                    background: on ? "rgba(140,115,85,0.2)" : "transparent",
                    color: on ? "#F5F4F0" : "rgba(214,209,202,0.75)",
                    transition: "all 0.35s ease",
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </section>

        <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-32">
          <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            <AnimatePresence mode="popLayout">
              {list.map((p, i) => (
                <motion.article
                  key={p.id}
                  layout
                  data-testid={`project-card-${p.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.6, delay: (i % 6) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="group"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#1a1b1e]">
                    <motion.img
                      src={p.image}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      initial={{ scale: 1.02 }}
                      whileHover={{ scale: 1.06 }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121315]/70 via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 text-[#C9A876] text-[9px] tracking-[0.35em] uppercase px-3 py-1 border border-[#8C7355]/40 bg-[#121315]/40 backdrop-blur">
                      {p.discipline}
                    </span>
                    <div className="absolute inset-3 border border-[#F5F4F0]/10 pointer-events-none" />
                  </div>

                  <div className="mt-5 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif-display text-[#F5F4F0] text-2xl font-light tracking-tight">
                        {p.title}
                      </h3>
                      <p className="text-[#D6D1CA]/70 text-sm mt-3 font-body">{p.caption}</p>
                      <p className="text-[#C9A876]/80 text-[10px] tracking-[0.3em] uppercase mt-3">
                        {p.material}
                      </p>
                    </div>
                    <ArrowUpRight
                      size={20}
                      className="text-[#C9A876] shrink-0 mt-1 opacity-60 group-hover:opacity-100"
                      style={{ transition: "opacity 0.4s ease" }}
                    />
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>

          {list.length === 0 && (
            <p className="text-[#D6D1CA]/60 text-center py-32 font-body italic">
              No projects in this element yet — the archive is growing.
            </p>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
